import { faThumbtack } from "@fortawesome/pro-regular-svg-icons";
import { groupBy, last } from "lodash-es";

import { CopyTextIcon } from "src/base-components/CopyTextIcon";
import { Pill } from "src/base-components/Pill";
import { useCaseBlockScrollTo } from "src/cases/caseView/CaseBlockScrollHooks";
import { HistorySingleEntry } from "src/cases/caseView/history/HistoryItemFrame";
import { useCase } from "src/cases/queries/cases";
import { HistoryEntryPinnedEventsGroup as PinnedEventsUpdatedGroupType } from "src/cases/queries/casesTypes";
import { PinnedEvent } from "src/clients/case-management";
import { Tooltip } from "src/design-system/Tooltip";
import { useEventSchemas } from "src/eventsCatalogue/queries";
import { findEventSchema, getEventIcon } from "src/eventsCatalogue/utils";
import { CasePageParams } from "src/router/urls";
import { useParamsDecode } from "src/utils/useParamsDecode";

export const PinnedEventsUpdatedGroup: React.FC<{
  entry: PinnedEventsUpdatedGroupType;
  isLast: boolean;
}> = ({ entry, isLast }) => {
  "use memo";

  const { events } = entry;
  const { case_id: caseId } = useParamsDecode<CasePageParams>();
  const { data: caseData } = useCase(caseId);
  const { data: eventSchemas } = useEventSchemas();
  const scrollToBlock = useCaseBlockScrollTo();

  // Use the first event (latest chronologically, since events are in reverse order)
  const firstEvent = last(events);

  if (!firstEvent) {
    // If such happened, then we have nothing to display
    return null;
  }

  // Consolidate all added and removed events from all events in the group
  const allAddedEvents = events.flatMap(
    (event) => event.event_data.added_events,
  );
  const allRemovedEvents = events.flatMap(
    (event) => event.event_data.removed_events,
  );

  const eventData = firstEvent.event_data;
  const blockKey = eventData.block_key;

  // Resolve block_key to block name
  const blockConfig =
    caseData?.data.template_version?.case_blocks_configs?.[blockKey];
  const blockName = blockConfig?.title ?? blockKey;

  // Create tooltip content with Pinned/Unpinned sections
  const tooltipBody = (
    <div className="flex flex-col gap-y-3">
      {allAddedEvents.length > 0 && (
        <div className="flex flex-col gap-y-1.5">
          <div className="font-inter-semibold-13px">Pinned Events</div>
          <EventList eventSchemas={eventSchemas} events={allAddedEvents} />
        </div>
      )}
      {allRemovedEvents.length > 0 && (
        <div className="flex flex-col gap-y-1.5">
          <div className="font-inter-semibold-13px">Unpinned Events</div>
          <EventList eventSchemas={eventSchemas} events={allRemovedEvents} />
        </div>
      )}
    </div>
  );

  // Build headline with pill wrapped in Tooltip
  const headline = (
    <>
      <Tooltip body={tooltipBody} asChild>
        <span>
          <Pill dataLoc="pinned-events-pill" size="sm" variant="gray">
            <Pill.Icon icon={faThumbtack} />
            <Pill.Text>Pinned Events</Pill.Text>
          </Pill>
        </span>
      </Tooltip>{" "}
      in{" "}
      <Pill size="sm" variant="gray" onClick={() => scrollToBlock(blockKey)}>
        <Pill.Text>{blockName}</Pill.Text>
      </Pill>{" "}
      were modified
    </>
  );

  return (
    <HistorySingleEntry
      actorId={eventData.updated_by_user_id}
      eventType={firstEvent.event_type}
      headline={headline}
      icon={faThumbtack}
      isLast={isLast}
      timestamp={firstEvent.timestamp}
    />
  );
};

const MAX_DISPLAY_COUNT = 5;

const EventList: React.FC<{
  events: PinnedEvent[];
  eventSchemas: ReturnType<typeof useEventSchemas>["data"];
}> = ({ events, eventSchemas }) => {
  "use memo";

  // Group events by event_type
  const eventsByType = groupBy(events, "event_type");

  return (
    <div className="flex flex-col gap-y-2">
      {Object.entries(eventsByType).map(([eventType, eventsOfType]) => {
        const schema = findEventSchema(eventType, eventSchemas);
        const displayName = schema?.display_name_plural || eventType;
        const icon = schema ? getEventIcon(schema) : undefined;

        const displayedEvents = eventsOfType.slice(0, MAX_DISPLAY_COUNT);
        const remainingCount = eventsOfType.length - MAX_DISPLAY_COUNT;

        return (
          <div key={eventType} className="flex flex-col gap-y-1">
            <div className="font-inter-medium-13px text-gray-300">
              {displayName}
            </div>
            <div className="flex flex-wrap gap-1">
              {displayedEvents.map((event) => (
                <Pill
                  key={event.id}
                  dataLoc={`pinned-event-${event.id}`}
                  size="sm"
                  variant="dark-gray"
                >
                  {icon && <Pill.Icon icon={icon} />}
                  <Pill.Text fontType="code">{event.id}</Pill.Text>
                  <CopyTextIcon
                    feedback="inline-only-checkmark"
                    size="3xs"
                    value={event.id}
                  />
                </Pill>
              ))}
              {remainingCount > 0 && (
                <Pill size="sm" variant="dark-gray">
                  <Pill.Text>+{remainingCount}</Pill.Text>
                </Pill>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};
